import api, { ApiResponse } from './api';
import { getEvidenceDisplayName, parseLocalToUTC } from '@safetyhub/shared';
import type { AnalysisRegion, LegalBasisSource, PhotoPurposeInUse, PhotoPurposeSettable, ReportGate } from '@safetyhub/shared';
import type { BadgeTone } from '@safetyhub/ui';

// 점검 상태
export enum InspectionStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING_REVIEW = 'PENDING_REVIEW',
  COMPLETED = 'COMPLETED',
}

// 위험요인 심각도
export enum FindingSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
  GOOD = 'GOOD',
}

// 발견사항 유형
export enum FindingType {
  HAZARD = 'HAZARD',
  BEST_PRACTICE = 'BEST_PRACTICE',
}

// 분석 상태
export enum AnalysisStatus {
  PENDING = 'PENDING',
  ANALYZING = 'ANALYZING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

// 검수 상태
export enum ReviewStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

// 법령 검색 상태
export enum LawSearchStatus {
  PENDING = 'PENDING',
  SEARCHING = 'SEARCHING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

// 점검 접근 유형
export type InspectionAccessType = 'full' | 'readonly' | 'none';

// 점검자 정보
export interface Inspector {
  id: string;
  deletedAt?: string | null;
  member: {
    name: string;
  };
}

// 현장 정보
export interface Site {
  id: string;
  name: string;
  address?: string;
}

// 점검 사진
export interface InspectionPhoto {
  id: string;
  inspectionId: string;
  url: string;
  thumbnailUrl?: string;
  status: string;
  analysisStatus: AnalysisStatus;
  isReviewed: boolean;
  reviewedAt?: string;
  createdAt: string;
  /** 사진 업로더 ID (권한 체크용) */
  addedById?: string;
  /** 사진 업로더 정보 */
  addedBy?: {
    id: string;
    name: string;
  };
  /**
   * 사진 용도 (위험요인 점검 / 우수사례).
   *
   * ⚠️ **DB enum보다 좁다** — 휴면 값(`OVERVIEW_PHOTO`)을 뺀 「오늘 쓰는 용도」다. 그 값으로
   * 만들어진 사진이 **한 번도 없어서**(prod·dev 둘 다 0건) 오늘 참이고, 되살리면 이 타입이
   * 거짓이 된다. 그때 볼 자리는 `@safetyhub/shared`의 `PHOTO_PURPOSES_DORMANT` JSDoc의
   * 「되살릴 때 해야 할 일」 다섯이다(이 화면의 이진 분기가 그중 셋째다).
   */
  purpose?: PhotoPurposeInUse;
  /** 사진 추가 시점 스냅샷 이름 (서버 신설 필드) */
  addedBySnapshotName?: string | null;
  /** AI 위험요인 요약 */
  riskSummary?: string;
  /** AI 개선방안 요약 (이미지 생성/임베딩 검색용) */
  improvementPlan?: string;
  /** AI 위험요인 유형 분류 */
  riskTypes?: string[];
  /** 장소 */
  location?: { id: string; name: string } | null;
  /** 상세 위치 */
  detailLocation?: { id: string; name: string } | null;
  /** 대상 (설비/기계 등) */
  equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
  /** 사진에 직접 저장된 공종/공정 (대상 없이도 설정 가능) */
  processType?: string | null;
  /**
   * 중점 분석 «영역» — 지정돼 있으면 다음 분석이 **사진 전체를 보내고** 이 사각형을 빨갛게 표시해
   * 그 안을 **중점**으로 보게 한다(없으면 표시 없이 전체 분석).
   *
   * ⚠️ 「그 안**만** 본다」로 쓰지 말 것 — 2026-10-06에 자르기(크롭)에서 표시(마커)로 바꿨고
   * (결정 `2026-10-06-analysis-region-switches-from-cropping-to-markers`), 범위 안에 위험요인이
   * 없으면 **전체 분석 결과로 조용히 돌아간다**. 화면 문구 쪽 탐지기는
   * `components/inspection/shared/analysis-region/__tests__/regionCopyDoesNotClaimCropping.test.ts`
   * 인데 그 가드는 **주석을 걷은 뒤**를 보므로 이 줄 같은 주석은 잡지 못한다(그래서 손으로 적는다).
   *
   * 서버가 따로 실어 주는 것이 아니라 `getInspection`의 `photos`가 `include`만 쓰고 `select`
   * 화이트리스트가 없어 **스칼라 전량이 그대로 온다**(`inspections.service.ts` photos include,
   * 후처리도 `...photo` spread). 그래서 이 줄은 **타입 선언뿐**이고 값은 이미 와 있었다.
   *
   * ⚠️ `BoundingBox`와 키 이름이 다르다(`w`/`h` ↔ `width`/`height`) — 아래 선언과 나란히 읽을 것.
   */
  analysisRegion?: AnalysisRegion | null;
}

/** 위험 영역 좌표 (정규화된 값 0.0~1.0) */
export interface BoundingBox {
  x: number;      // 좌상단 X 좌표
  y: number;      // 좌상단 Y 좌표
  width: number;  // 너비
  height: number; // 높이
}

// AI 분석 상세 정보 (위험성 평가, 개선대책, 법적근거 등)
export interface AIAnalysisDetail {
  location?: {
    area?: string;
    detail?: string;
  };
  /** 위험 영역 좌표 (사진 내 위치) */
  boundingBox?: BoundingBox;
  riskAssessment?: {
    likelihood: number;
    likelihoodLabel?: string;
    severity: number;
    severityLabel?: string;
    riskLevel: number;
    riskLevelLabel?: string;
    action?: string;
  };
  recommendation?: {
    measures?: string[];
    /**
     * AI judge 메타데이터 포함 measure 객체 배열.
     * measures string[]과 1:1 대응되며, isPrimary로 자동 추천 표시.
     * legacy finding은 undefined일 수 있음 → measures string[]로 fallback.
     */
    measureItems?: Array<{
      text: string;
      isPrimary?: boolean;
      source?: 'ai' | 'hk';
      score?: number;
      reason?: string;
    }>;
    legalBasis?: Array<{
      type: string;
      article?: string;
      summary: string;
      url?: string;
      score?: number;
      reason?: string;
      /** 출처 — 'default'는 판정 채택 0건 폴백(기본 근거 배지), 없으면 judge(shared LegalBasis.source) */
      source?: LegalBasisSource;
    }>;
  };
  confidence?: number;
  /** HK 프리매칭 성공 여부 */
  knowledgeBaseMatched?: boolean;
  /** HK 대책 개수 (measures 앞쪽 N개가 HK 출처) */
  hkMeasureCount?: number;
}

// 수정된 필드 정보 (원본 값 포함)
export interface ModifiedFieldInfo {
  field: string;
  originalValue: unknown;
  currentValue: unknown;
}

// Finding 수정 정보
export interface FindingModifications {
  isModified: boolean;
  modifiedFields: ModifiedFieldInfo[];
}

// 발견사항 (위험요인)
export interface InspectionFinding {
  id: string;
  inspectionId: string;
  photoId?: string;
  /**
   * 발견사항이 붙은 사진 — **서버가 내리는 네 필드만** 든다(전체 `InspectionPhoto`가 아니다).
   *
   * 정본은 `apps/server/src/inspections/inspections.service.ts` `findOne`의
   * `findings.include.photo.select`다. 그 `select`를 2026-10-08에 네 필드로 좁혔는데
   * (사진 행 전체를 발견사항마다 싣고 있었다 — `analysis_result` jsonb가 중복으로 실려
   * 응답이 433.3KB였다), **이 선언이 전체 타입이면 그 좁힘을 아무것도 지키지 않는다**:
   * 5번째 필드를 읽는 코드가 **컴파일되고 런타임에 `undefined`**가 된다.
   * ⇒ 좁혀 두어야 **tsc가 진짜 의존을 드러낸다.**
   *
   * ⚠️ **두 축이 다르다** — 서버 쪽은 「`select`가 네 필드인가」를 `inspections.service.spec.ts`의
   * `[되돌림] findings.photo는 네 필드만 select`가 **호출 인자 단언**으로 지키고(prisma mock은
   * `select`를 무시하므로 결과값으로는 못 잰다), web 쪽은 **이 선언 + tsc**가 지킨다.
   * 둘 중 하나만 있으면 반대쪽이 조용히 어긋난다.
   *
   * 이 타입을 쓰는 엔드포인트 둘:
   * - `getInspection`(서버 `findOne`) — **이 네 필드가 전부**다. 소비처는
   *   `hooks/useInspectionDetailPage.ts`(`url`·`thumbnailUrl`·`addedById`·`id`)
   * - `getPreviousUnresolvedFindings`(서버 `inspection-finding.service.ts`) — 서버가 사진 행
   *   **전체**를 내리지만 읽는 자리가 `toPreviousFindingContentData`의 `f.photo?.url` **하나**라
   *   같은 타입을 쓴다(아래 `PreviousUnresolvedFinding`이 이것을 extends한다)
   *
   * ⇒ **5번째 필드가 필요해지면** 타입을 가르거나(엔드포인트별로) 이 Pick과 서버 `select`를
   * **함께** 넓혀라. 한쪽만 넓히면 타입은 통과하고 값은 안 온다.
   */
  photo?: Pick<InspectionPhoto, 'id' | 'url' | 'thumbnailUrl' | 'addedById'>;
  category: string;
  subCategory?: string;
  description: string;
  severity: FindingSeverity;
  findingType?: 'HAZARD' | 'BEST_PRACTICE';
  aiGenerated: boolean;
  aiConfidence?: number;
  aiEdited?: boolean;
  aiAnalysisDetail?: AIAnalysisDetail;
  resolutionStatus: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolvedAt?: string;
  resolvedBy?: string;
  // 해결 증빙
  resolutionPhotoUrl?: string;
  resolutionPhotoId?: string;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  notes?: string;
  resolvedByUser?: { id: string; name: string; deletedAt?: string | null };
  // 조치결과 제출 추적 (어느 점검에서 마지막으로 조치결과를 입력했는지)
  lastActionInspectionId?: string;
  // 재발견 추적
  originalFindingId?: string;
  recurrenceCount?: number;
  firstOccurrenceDate?: string;
  createdAt: string;
  // AI 원본 대비 수정 정보
  modifications?: FindingModifications;
  // 법령 검색 상태 (비동기 처리)
  lawSearchStatus?: LawSearchStatus;
  // Phase N3: 정규화 위험 점수 (1~10, HAZARD만, BEST_PRACTICE는 null)
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  // Soft delete
  deletedAt?: string;
  deletedBy?: string;
  // 선택된 개선대책 목록 (피처 플래그 report-finding-selection)
  // 각 항목은 선택한 사용자 정보 포함 (서버: 2026-04-17 구조 변경)
  selectedMeasures?: SelectedMeasureEntry[];
  // 증빙 행위자 이름 스냅샷 (서버 신설 필드)
  /** finding 추가 시점 스냅샷 이름 (탈퇴/purge 후에도 원본 유지) */
  addedBySnapshotName?: string | null;
  /** finding 조치 시점 스냅샷 이름 (탈퇴/purge 후에도 원본 유지) */
  resolvedBySnapshotName?: string | null;
  // TBM 분류 축 (2026-09-02 규약 ③) — 값 3벌 중 발견사항 레벨 두 벌
  /** 분석이 후보 목록에서 고른 작업 분류. 불변 기록 — 이견 표시·일치율 측정의 원본 */
  analyzedWorkCategory?: string | null;
  /** 사람이 발견사항별로 확정한 작업 분류. null이면 확정 해제(사다리 폴백) */
  confirmedWorkCategory?: string | null;
}

// 개선대책 선택 항목 (해제 권한 검증용)
export interface SelectedMeasureEntry {
  measure: string;
  selectedById: string;
  selectedByName: string | null;
  // 선택자의 역할. null = orphan(현재 비참여자)
  selectedByRole: 'OWNER' | 'PRIMARY' | 'SECONDARY' | null;
  // 선택자의 현재 completionStatus. null = orphan(현재 비참여자)
  selectedByCompletionStatus?:
    | 'IN_PROGRESS'
    | 'REVIEWING'
    | 'COMPLETED'
    | null;
}

// 이전 미해결 발견사항 (추가 정보 포함)
export interface PreviousUnresolvedFinding extends InspectionFinding {
  inspection: {
    id: string;
    createdAt: string;
    completedAt?: string;
  };
  refinements?: Array<{
    refinedRisk: string;
    refinedMeasure: string;
    refinedLegalBasis: unknown;
  }>;
  /** 보고서 최종 위험요인 (override → refinement fallback) */
  reportRisk?: string;
  /** 보고서 최종 개선대책 (override → refinement fallback) */
  reportMeasure?: string;
}

/**
 * 이전 발견사항 → AI 요약 생성용 DTO 변환
 * 정제 데이터(reportRisk)가 있으면 우선 사용
 */
export function toPreviousFindingSummaryDto(f: PreviousUnresolvedFinding) {
  return {
    findingId: f.id,
    category: f.category,
    subCategory: f.subCategory,
    description: f.reportRisk || f.description,
    severity: f.severity,
    originalInspectionId: f.inspection.id,
    originalInspectionDate: f.inspection.createdAt,
    isResolved: f.resolutionStatus === 'COMPLIED',
    notes: f.notes,
  };
}

/**
 * 이전 발견사항 → 보고서 content 저장용 데이터 변환
 * 정제 데이터(reportRisk/reportMeasure)가 있으면 우선 사용
 */
export function toPreviousFindingContentData(f: PreviousUnresolvedFinding, currentInspectionId: string) {
  const originalMeasures = f.aiAnalysisDetail?.recommendation?.measures as string[] | undefined;
  return {
    findingId: f.id,
    category: f.category,
    subCategory: f.subCategory,
    description: f.reportRisk || f.description,
    severity: f.severity,
    originalInspectionId: f.inspection.id,
    originalInspectionDate: f.inspection.createdAt,
    isResolved: f.resolutionStatus === 'COMPLIED',
    resolvedAt: f.resolvedAt,
    notes: f.notes,
    measures: f.reportMeasure ? [f.reportMeasure] : originalMeasures,
    originalPhotoUrl: f.photo?.url,
    resolutionPhotoUrl: f.resolutionPhotoUrl,
    hasActionResult: f.lastActionInspectionId === currentInspectionId,
  };
}

// 위험도별 개수
export interface SeverityCounts {
  LOW: number;
  MEDIUM: number;
  HIGH: number;
  CRITICAL: number;
}

// 스케줄 변경 요청 타입
export type ScheduleChangeRequestType = 'RESCHEDULE' | 'CANCEL';
export type ScheduleChangeRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ScheduleChangeRequest {
  id: string;
  inspectionId: string;
  requestType: ScheduleChangeRequestType;
  currentDate: string;
  requestedDate?: string | null;
  reason: string;
  status: ScheduleChangeRequestStatus;
  rejectReason?: string | null;
  requestedById: string;
  requestedBy?: { id: string; name: string };
  respondedById?: string | null;
  respondedBy?: { id: string; name: string } | null;
  respondedAt?: string | null;
  reminderCount: number;
  lastReminderAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

// 스케줄 변경 요청 생성 입력
export interface CreateScheduleChangeRequestInput {
  requestType: ScheduleChangeRequestType;
  requestedDate?: string;
  reason: string;
}

// 스케줄 변경 요청 응답 입력
export interface RespondScheduleChangeRequestInput {
  approve: boolean;
  rejectReason?: string;
}

// 점검 목록 아이템
export interface InspectionListItem {
  id: string;
  companyId: string;
  siteId: string;
  site: Site;
  /** 점검 생성자 (PRIMARY collaborator와 별도로 보존되는 원작성자) */
  createdById: string;
  /** PRIMARY collaborator의 userId — 점검 대표 책임자 */
  inspectorId: string;
  inspector: Inspector;
  status: InspectionStatus;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
  photoCount: number;
  hazardCount: number;
  bestPracticeCount?: number;
  // 검수 상태
  reviewStatus: ReviewStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewer?: {
    id: string;
    name: string;
  };
  // 위험도별 발견사항 개수
  severityCounts?: SeverityCounts;
  /** 점검 참여자 목록 (Contributor + Participant 통합) */
  collaborators: InspectionCollaborator[];
  createdAt: string;
  updatedAt: string;
  /** 삭제 일시 (soft delete) */
  deletedAt?: string | null;
  /** 현장에서의 점검 순서 (N차) */
  sequenceNumber?: number;
  /** 점검 시작 가능 여부 (서버 계산: SCHEDULED + 예약일 ≤ 오늘 + 이전 점검 완료) */
  canStart?: boolean;
  /** 해당 현장에서 가장 최근 점검 여부 */
  isLatestInspection?: boolean;
  /**
   * 「보고서를 생성할 수 있나 + 안 되는 이유」 — **서버 판정**(`resolveReportGate`, shared 정본).
   *
   * ⚠️⚠️ **2026-10-09(`a8061c0c4`)부터 이 값에 권한 축이 «들어 있다» — 소비처는 AND하지 말 것.**
   * 서버가 `resolveCanCreateReport`로 집행 식(`신원 축 ∨ (현장 scope ∧ report.create)`)을 미러해
   * 넘기므로 **`disabledReason`이 「권한이 없다」까지 말한다.** 여기에 화면 권한식을 AND하면
   * **집행보다 좁아진다** — 그 AND가 prod **46건**을 거짓 차단하고 있었고(결정
   * `2026-10-09-the-enforcement-expression-is-the-canonical-gate.md`) 그래서 걷었다.
   *
   * > 종전 서술(~2026-10-09): 「이 값만으로 메뉴를 열지 말 것 — 자기 권한 판정과 **AND**해야
   * > 한다. 서버는 `canCreateReport: true`를 넘기므로 `disabledReason`은 권한을 말하지 않는다.」
   * > 그 시절의 사실이고, **그 AND가 권한식 사본 셋을 만든 원인**이었다.
   *
   * ⚠️ **단 그 면제는 이 필드를 «받는» 경로에만 참이다** — 아래 표의 스케줄처럼 이 필드가 없는
   * 화면은 여전히 자기 권한을 판정해야 한다(없는 값으로는 AND할 것도 없다).
   *
   * ⚠️ **옵셔널인 이유는 「배포 과도기」가 아니라 «그 축이 영구히 없는 응답 경로»가 있어서다.**
   * 점검을 돌려주는 경로가 셋이고 이 필드를 싣는 것은 둘이다:
   *
   * | 경로 | 이 필드 | 쓰는 화면 |
   * |---|---|---|
   * | `inspections.service.findAll` | 있다 | 점검 목록 · `/reports/new` 위저드 |
   * | `inspections.service.findOne` | 있다 | 점검 상세 3 레이아웃 |
   * | `organizations.service.findInspectionSchedules` | **없다** | **스케줄 페이지** |
   *
   * 스케줄은 `inspections` 모듈이 아닌 **별개 엔드포인트**(`GET /organizations/:id/inspection-schedules`)
   * 를 쓰고, 그 쿼리의 `select`엔 게이트 재료(`findings`·`photos`·`_count`·`collaborators.completionStatus`)
   * 가 **없다**. ⇒ 그 화면에서 이 값은 `undefined`다.
   *
   * ⚠️ 그래서 소비처의 폴백은 **「막는 쪽」이 아니라 「종전 판정」**이어야 한다. 없는 필드로 메뉴를
   * 잠그면 그 화면의 기능이 통째로 꺼진다. 폴백이 선택·서명 축을 못 보는 것은 **알려진 한계**이고,
   * 짝 안전망은 서버 생성 경로의 선택 확인이다(그 자리가 실제로 거부한다).
   *
   * ⚠️ **그 폴백 가지는 권한도 못 본다** — 그래서 스케줄 화면은 위 면제에서 **빠지고** 자기 권한
   * AND를 유지한다(`pages/schedules/components/DayInspectionList.tsx`가 그 근거의 정본). 그 결과
   * **그 화면의 답은 목록보다 좁다**(같은 46건을 거기서는 여전히 막는다) — 알려진 비대칭이다.
   * 🔖 되짚을 조건: 위 스케줄 쿼리에 재료가 들어오면 폴백과 **그 화면의 권한 AND를 함께** 걷는다
   *    (먼저 걷으면 그 사이 문이 열린다) — 그때 이 표와 그 비대칭 서술을 함께 고칠 것.
   */
  reportGate?: ReportGate;
  /** 시작을 차단하는 이전 미완료 점검 ID */
  blockingInspectionId?: string;
  /** 시작을 차단하는 이전 미완료 점검 차수 */
  blockingInspectionOrder?: number;
  /** Owner가 배정한 점검인지 여부 (변경 요청 가능 여부 판단) */
  assignedById?: string | null;
  /** AI 분석 중점 카테고리 코드 배열 */
  focusCategories?: string[];
  /** PENDING 상태의 스케줄 변경 요청 (달력 배지용) */
  hasPendingScheduleChangeRequest?: boolean;
  /** 연결된 보고서 목록 (달력 배지용) */
  reportInspections?: Array<{
    report: {
      id: string;
      status: string;
      /** 보고서 content (TBM workType 등) */
      content?: Record<string, unknown>;
      /** TBM 누적 참석자 수 */
      attendeeCount?: number;
      /** TBM 교육 세션 총 수 */
      totalSessions?: number;
      /** TBM 교육 세션 완료 수 */
      completedSessions?: number;
      /**
       * 작업 유형(공종) — **Composer 전환본의 축**. 레거시 TBM은 `content.workType`에 있지만
       * COMPOSED에는 그 키가 없고 이 컬럼이 정본이다(`tbm-distribution-setup.service`의
       * 「composed엔 content.workType 부재」). 응답이 통짜 content 대신 이 스칼라를 싣는다.
       */
      splitGroupLabel?: string | null;
      /**
       * 이어질 흐름의 선언 키 — 이 화면이 TBM 갈래를 「레거시 type ∪ 흐름 선언」으로 가르는
       * 축(`isTbmEducationDocument`). 없으면 COMPOSED TBM이 교육 문서로 인식되지 않는다.
       */
      postSubmitFlowKey?: string | null;
    };
  }>;
  _count: {
    photos: number;
    findings: number;
    collaborators?: number;
  };
}

// 점검 상세
export interface Inspection extends InspectionListItem {
  photos: InspectionPhoto[];
  findings: InspectionFinding[];
  /** 접근 권한 유형 (full: 편집 가능, readonly: 읽기 전용, none: 접근 불가) */
  accessType?: InspectionAccessType;
  /** 보고서 생성 여부 (생성된 경우 사진 추가 불가) */
  hasReport?: boolean;
  /** 다음 점검 날짜 (ISO 8601) */
  nextInspectionDate?: string | null;
  /** 다음 점검에 보고서가 존재하는지 여부 */
  nextInspectionHasReport?: boolean;
  /** Phase N3: 유효 위험성평가 설정 (inspection → site → org → system default 계층) */
  effectiveRiskAssessmentConfig?: import('@safetyhub/shared').RiskAssessmentConfig | null;
  /** 점검별 위험성평가 config override ID (null이면 fallback 사용) */
  riskAssessmentConfigId?: string | null;
}

// 점검 생성 입력
export interface CreateInspectionInput {
  companyId: string;
  siteId: string;
  scheduledAt?: string;
  notes?: string;
  focusCategories?: string[];
  /** Owner가 대리 예약 시 지정할 안전관리자 ID */
  inspectorId?: string;
  /** 복수 참가자 userIds */
  participantUserIds?: string[];
  /** 정 담당자 userId */
  primaryParticipantUserId?: string;
}

// 점검 수정 입력
export interface UpdateInspectionInput {
  status?: InspectionStatus;
  scheduledAt?: string;
  notes?: string;
  focusCategories?: string[];
  /** 점검별 위험성평가 config override (null = 점검 자체 값 제거) */
  riskAssessmentConfigId?: string | null;
}

// 발견사항 추가 입력
export interface AddFindingInput {
  photoId?: string;
  category: string;
  subCategory?: string;
  description: string;
  /** @deprecated Phase N5a 호환용. aiSeverityScore 전달 시 서버에서 자동 계산됨 */
  severity?: FindingSeverity;
  findingType?: 'HAZARD' | 'BEST_PRACTICE';
  aiGenerated?: boolean;
  aiConfidence?: number;
  /** Phase N3+: 피해 강도 점수 (1~10) */
  aiSeverityScore?: number;
  /** Phase N3+: 발생 가능성 점수 (1~10) */
  aiLikelihoodScore?: number;
  /** 위험 영역 좌표 (수동 추가 시 사진 전체 영역) */
  boundingBox?: BoundingBox;
}

// 발견사항 수정 입력
export interface UpdateFindingInput {
  category?: string;
  subCategory?: string;
  description?: string;
  severity?: FindingSeverity;
  location?: {
    area?: string;
    detail?: string;
  };
  riskAssessment?: {
    likelihood?: number;
    severity?: number;
    riskLevel?: number;
  };
  /** 개선대책 배열 (전체 교체) */
  measures?: string[];
  /** 관련법규 배열 (전체 교체) */
  legalBasis?: Array<{
    type: string;
    article?: string;
    summary: string;
    url?: string;
  }>;
  /** AI 원본으로 되돌리기 여부 (알림 메시지 구분용) */
  isRevert?: boolean;
  /** 위험 영역 좌표 (0~1 정규화된 값, null이면 삭제) */
  boundingBox?: BoundingBox | null;
  /**
   * 사람 확정 작업 분류 (TBM 분류 축 규약 ③④). null이면 확정 해제 → 사다리 폴백.
   * analyzedWorkCategory(분석 불변값)는 서버 DTO에 없다 — 쓰기 경로 자체가 가드다.
   */
  confirmedWorkCategory?: string | null;
}

// 목록 조회 필터
/**
 * 목록 정렬 축 — 서버 화이트리스트(`inspections.service.ts`의 `INSPECTION_SORT_FIELDS`)와 같은
 * 값이어야 한다. 키 이름은 데스크톱 표 헤더가 쓰는 이름이고, `inspectionDate`는 표의 '점검일'
 * 칸이 그리는 값(`createdAt`)과 같은 축이다.
 */
export const INSPECTION_SORT_FIELDS = [
  'siteName',
  'inspectionDate',
  'inspectorName',
  'status',
  'createdAt',
] as const;

export type InspectionSortField = (typeof INSPECTION_SORT_FIELDS)[number];

export function isInspectionSortField(value: string): value is InspectionSortField {
  return (INSPECTION_SORT_FIELDS as readonly string[]).includes(value);
}

export interface InspectionFilters {
  siteId?: string;
  status?: InspectionStatus;
  inspectorId?: string;
  limit?: number;
  offset?: number;
  /** 정렬 축. 없으면 서버 기본(최신순). */
  sortField?: InspectionSortField;
  /** 정렬 방향. `sortField`가 있을 때만 의미가 있다. */
  sortDirection?: 'asc' | 'desc';
  /** 삭제된 점검 포함 여부 */
  includeDeleted?: boolean;
  /** 장소 ID 필터 */
  locationId?: string;
  /** 설비 ID 필터 */
  equipmentId?: string;
  /** 날짜 필터 (YYYY-MM-DD, KST 기준). effectiveDate(scheduledAt ?? createdAt)가 해당 날짜인 점검만 반환 */
  scheduledDate?: string;
  /**
   * 기간 시작 (현지 **YYYY-MM-DD**). 그 날 00:00:00부터 — UTC 변환은 아래 요청 빌더가 한다.
   * effectiveDate(scheduledAt ?? createdAt) 기준. 한쪽만 줘도 된다(이후 전부).
   */
  startDate?: string;
  /** 기간 종료 (현지 **YYYY-MM-DD**). 그 날 23:59:59까지. 한쪽만 줘도 된다(이전 전부). */
  endDate?: string;
}

/**
 * 기간 필터의 현지 날짜 → UTC ISO 변환.
 *
 * 날짜만 고르는 UI라도 경계 시각을 붙여 UTC로 바꿔 보낸다(CLAUDE.md '날짜만 선택하는 UI 처리').
 * `YYYY-MM-DD`를 그대로 보내면 서버가 자정 UTC로 읽어 KST 기준 하루가 어긋난다.
 *
 * 변환을 목록 훅이 아니라 **요청 빌더에 두는 이유**: 목록과 통계 카드가 같은 필터를 각자
 * 변환하면 두 수가 갈린다("완료 3건"을 눌렀는데 0행). 여기 한 곳에서만 변환한다.
 *
 * 시간대는 shared 기본값(Asia/Seoul)을 쓴다 — 같은 엔드포인트의 `scheduledDate`가 이미
 * KST 고정으로 해석하므로, 두 날짜 필터가 같은 기준을 봐야 결과가 어긋나지 않는다.
 */
function toUtcRangeBound(localDate: string, boundary: 'start' | 'end'): string | null {
  const time = boundary === 'start' ? 'T00:00:00' : 'T23:59:59';
  try {
    return parseLocalToUTC(`${localDate}${time}`).toISOString();
  } catch {
    // 입력 중인 부분 날짜(`2026-0`)는 필터를 걸지 않는다 — 던지면 목록이 통째로 죽는다.
    return null;
  }
}

// 목록 응답 메타
export interface ListMeta {
  total: number;
  limit: number;
  offset: number;
}

/**
 * 점검 목록 조회
 */
export async function getInspections(
  companyId: string,
  filters?: InspectionFilters
): Promise<ApiResponse<InspectionListItem[]> & { meta?: ListMeta }> {
  const params = new URLSearchParams();
  params.set('companyId', companyId);

  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.inspectorId) params.set('inspectorId', filters.inspectorId);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));
  if (filters?.includeDeleted) params.set('includeDeleted', 'true');
  if (filters?.locationId) params.set('locationId', filters.locationId);
  if (filters?.equipmentId) params.set('equipmentId', filters.equipmentId);
  if (filters?.scheduledDate) params.set('scheduledDate', filters.scheduledDate);
  if (filters?.startDate) {
    const from = toUtcRangeBound(filters.startDate, 'start');
    if (from) params.set('startDate', from);
  }
  if (filters?.endDate) {
    const to = toUtcRangeBound(filters.endDate, 'end');
    if (to) params.set('endDate', to);
  }
  if (filters?.sortField) {
    params.set('sortField', filters.sortField);
    params.set('sortDirection', filters.sortDirection ?? 'desc');
  }

  const response = await api.get<ApiResponse<InspectionListItem[]> & { meta?: ListMeta }>(
    `/inspections?${params}`
  );
  return response.data;
}

/**
 * 점검 상세 조회
 */
export async function getInspection(
  id: string,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  const response = await api.get<ApiResponse<Inspection>>(
    `/inspections/${id}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 점검 생성
 */
export async function createInspection(
  data: CreateInspectionInput
): Promise<ApiResponse<Inspection>> {
  const response = await api.post<ApiResponse<Inspection>>('/inspections', data);
  return response.data;
}

/**
 * 점검 수정
 */
export async function updateInspection(
  id: string,
  data: UpdateInspectionInput,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  const response = await api.patch<ApiResponse<Inspection>>(
    `/inspections/${id}?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 점검 참여자 일괄 수정 (진행 중 점검의 담당자 편집)
 */
export interface UpdateParticipantsInput {
  participants: Array<{ userId: string; role: 'PRIMARY' | 'SECONDARY' }>;
}

export interface UpdateParticipantsResponse {
  collaborators: InspectionCollaborator[];
}

export async function updateInspectionParticipants(
  inspectionId: string,
  companyId: string,
  data: UpdateParticipantsInput
): Promise<ApiResponse<UpdateParticipantsResponse>> {
  const response = await api.put<ApiResponse<UpdateParticipantsResponse>>(
    `/inspections/${inspectionId}/participants?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 점검 삭제 (DRAFT 상태만 가능)
 */
export async function deleteInspection(
  id: string,
  companyId: string
): Promise<ApiResponse<{ success: boolean }>> {
  const response = await api.delete<ApiResponse<{ success: boolean }>>(
    `/inspections/${id}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 점검 복원 (soft delete 취소)
 */
export async function restoreInspection(
  id: string,
  companyId: string
): Promise<ApiResponse<{ success: boolean }>> {
  const response = await api.post<ApiResponse<{ success: boolean }>>(
    `/inspections/${id}/restore?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 사진 업로드
 * @param skipAnalysis true면 업로드만 하고 AI 분석은 시작하지 않음 (나중에 startAnalysis 호출 필요)
 * @param focusCategories AI 분석 중점 카테고리 코드 배열
 */
export async function uploadPhoto(
  inspectionId: string,
  file: File,
  companyId: string,
  options?: {
    skipAnalysis?: boolean;
    /**
     * **비기본** 용도 — 생략하면 서버 DB default(`HAZARD_CHECK`)가 된다. `HAZARD_CHECK`가 없는 것은
     * 누락이 아니다(정본 주석: `@safetyhub/shared`의 `PhotoPurposeSettable`).
     */
    purpose?: PhotoPurposeSettable;
    focusCategories?: string[];
    /**
     * 중점 분석 영역 — 지정하면 첫 분석부터 이 사각형을 빨갛게 표시해 **중점**으로 보게 한다
     * (사진은 통째로 보낸다 — 위 `analysisRegion` 선언의 「그 안만으로 쓰지 말 것」 주석 참조).
     *
     * ⚠️ 이 경로는 **로컬 storage 환경(dev)에서만 쓰인다** — prod는 R2라
     * `uploadPhotosParallel`이 `prepare-upload`로 가고 거기서 사진 행이 만들어진다. 그래서 이
     * 자리를 빼먹어도 prod는 정상으로 보이고 **dev에서만 영역이 조용히 안 실린다**(어느 쪽도
     * 400을 내지 않는다 — 영역은 optional이라 그냥 전체 분석이 된다).
     */
    analysisRegion?: AnalysisRegion | null;
    locationId?: string | null;
    equipmentId?: string | null;
    detailLocationId?: string | null;
    processType?: string | null;
  }
): Promise<ApiResponse<InspectionPhoto>> {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.set('companyId', companyId);
  if (options?.skipAnalysis) {
    params.set('skipAnalysis', 'true');
  }
  if (options?.purpose) {
    params.set('purpose', options.purpose);
  }
  if (options?.focusCategories && options.focusCategories.length > 0) {
    params.set('focusCategories', options.focusCategories.join(','));
  }
  // 영역만 CSV가 아니라 **JSON 문자열**이다 — CSV면 w와 h를 뒤바꿔 보내도 「유효한 사각형」이라
  // 400도 경고도 없이 엉뚱한 자리가 잘린다(서버 `parseAnalysisRegionQuery` JSDoc).
  if (options?.analysisRegion) {
    params.set('analysisRegion', JSON.stringify(options.analysisRegion));
  }
  if (options?.locationId) {
    params.set('locationId', options.locationId);
  }
  if (options?.equipmentId) {
    params.set('equipmentId', options.equipmentId);
  }
  if (options?.detailLocationId) {
    params.set('detailLocationId', options.detailLocationId);
  }
  if (options?.processType) {
    params.set('processType', options.processType);
  }

  const response = await api.post<ApiResponse<InspectionPhoto>>(
    `/inspections/${inspectionId}/photos?${params}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * 여러 사진 업로드
 * @param skipAnalysis true면 업로드만 하고 AI 분석은 시작하지 않음 (나중에 startAnalysis 호출 필요)
 */
export async function uploadPhotos(
  inspectionId: string,
  files: File[],
  companyId: string,
  onProgress?: (uploaded: number, total: number) => void,
  // `purpose`는 **비기본** 용도 — 의미는 `uploadPhoto`의 같은 자리 주석이 소유한다.
  options?: { skipAnalysis?: boolean; purpose?: PhotoPurposeSettable }
): Promise<InspectionPhoto[]> {
  const results: InspectionPhoto[] = [];

  for (let i = 0; i < files.length; i++) {
    const response = await uploadPhoto(inspectionId, files[i], companyId, options);
    if (response.success && response.data) {
      results.push(response.data);
    }
    onProgress?.(i + 1, files.length);
  }

  return results;
}

/**
 * 점검 사진 분석 시작
 * 모든 PENDING 상태 사진들을 배치로 분석 큐에 추가
 */
export async function startAnalysis(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ queuedCount: number; analysisMode: string; jobCount: number }>> {
  const response = await api.post<ApiResponse<{ queuedCount: number; analysisMode: string; jobCount: number }>>(
    `/inspections/${inspectionId}/start-analysis?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 발견사항(위험요인) 추가
 */
export async function addFinding(
  inspectionId: string,
  data: AddFindingInput,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.post<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 발견사항(위험요인) 해결 처리
 */
export async function resolveFinding(
  inspectionId: string,
  findingId: string,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/resolve?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 발견사항(위험요인) 수정
 */
export async function updateFinding(
  inspectionId: string,
  findingId: string,
  data: UpdateFindingInput,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 발견사항(위험요인) 삭제
 */
export async function deleteFinding(
  inspectionId: string,
  findingId: string,
  companyId: string
): Promise<ApiResponse<{ success: boolean }>> {
  const response = await api.delete<ApiResponse<{ success: boolean }>>(
    `/inspections/${inspectionId}/findings/${findingId}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 점검 상태 변경 헬퍼
 */
export async function changeInspectionStatus(
  id: string,
  status: InspectionStatus,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  return updateInspection(id, { status }, companyId);
}

/**
 * 예약 점검 시작 (SCHEDULED → IN_PROGRESS)
 */
export async function startScheduledInspection(
  id: string,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  const response = await api.patch<ApiResponse<Inspection>>(
    `/inspections/${id}/start?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 오늘 예정 점검 캐시 키의 **루트** — react-query 키의 첫 원소.
 *
 * ⚠️ 이 문자열이 흩어지면 **바꿀 때 조용히 깨진다.** 읽는 쪽은 전체 키
 * (`todayScheduledQueryKey`)를 쓰고 무효화하는 쪽 넷은 **루트 prefix만** 쓰는데(모든 회사의
 * 캐시를 한 번에 무효화해야 한다 — react-query는 prefix 매칭이다), 종전엔 그 넷이 리터럴
 * `['todayScheduledInspections']`를 손으로 적고 있었다. 즉 루트를 고치면 **네 자리가 아무것도
 * 무효화하지 않는 상태**가 되고(위젯이 stale) 아무도 깨지지 않는다 — 2026-10-05 리뷰에서
 * 위젯 주석이 「리터럴을 흩지 않고 여기서만 만든다」고 적고 있는데 **거짓**임이 드러나 고쳤다.
 *
 * 키 헬퍼가 위젯이 아니라 여기 사는 이유: 소비자가 `components/`·`hooks/`·`pages/`에 흩어져
 * 있어 위젯 파일에 두면 `WebSocketProvider`가 `pages/Dashboard/...`를 import하는 층 역전이
 * 된다. 키는 **그것을 채우는 fetcher 옆**(바로 아래)이 제자리다.
 */
export const TODAY_SCHEDULED_QUERY_ROOT = 'todayScheduledInspections';

/** 조회용 전체 키 — 회사별로 캐시가 갈린다. */
export function todayScheduledQueryKey(companyId: string | undefined) {
  return [TODAY_SCHEDULED_QUERY_ROOT, companyId] as const;
}

/**
 * 무효화용 **루트** 키 — 회사를 가리지 않고 전부 무효화한다(prefix 매칭).
 *
 * ⚠️ 무효화에 `todayScheduledQueryKey(companyId)`를 쓰지 말 것 — 조직을 전환하거나
 * 빙의 중이면 다른 회사 캐시가 남는다.
 */
export function todayScheduledQueryRootKey() {
  return [TODAY_SCHEDULED_QUERY_ROOT] as const;
}

/**
 * 오늘 예정된 SCHEDULED 점검 목록 조회
 * organizations API를 통해 당월 데이터 조회 후 클라이언트에서 오늘 날짜 필터
 */
export async function getTodayScheduledInspections(
  organizationId: string
): Promise<ApiResponse<InspectionListItem[]>> {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const today = getLocalDateString(now);
  const response = await api.get<ApiResponse<InspectionListItem[]>>(
    `/organizations/${organizationId}/inspection-schedules?month=${month}`
  );
  if (response.data.success && response.data.data) {
    response.data.data = response.data.data.filter((insp) => {
      if (insp.status !== InspectionStatus.SCHEDULED) return false;
      const scheduledDate = insp.scheduledAt ? getLocalDateString(new Date(insp.scheduledAt)) : null;
      return scheduledDate === today;
    });
  }
  return response.data;
}

/**
 * 월간 스케줄 점검 목록 조회 (달력용)
 * month: "YYYY-MM" 형식
 */
export async function getMonthlyInspectionSchedules(
  organizationId: string,
  month: string,
  siteId?: string,
  inspectorId?: string,
  groupId?: string | null
): Promise<ApiResponse<InspectionListItem[]>> {
  const params = new URLSearchParams({ month });
  if (siteId) params.set('siteId', siteId);
  if (inspectorId) params.set('inspectorId', inspectorId);
  if (groupId) params.set('groupId', groupId);
  const response = await api.get<ApiResponse<InspectionListItem[]>>(
    `/organizations/${organizationId}/inspection-schedules?${params.toString()}`
  );
  return response.data;
}

/**
 * 점검 상태 한글 라벨
 */
export const inspectionStatusLabels: Record<InspectionStatus, string> = {
  [InspectionStatus.SCHEDULED]: '예약됨',
  [InspectionStatus.IN_PROGRESS]: '진행 중',
  [InspectionStatus.PENDING_REVIEW]: '검토 대기',
  [InspectionStatus.COMPLETED]: '완료',
};

/**
 * 위험요인 심각도 한글 라벨
 */
export const findingSeverityLabels: Record<FindingSeverity, string> = {
  [FindingSeverity.LOW]: '낮음',
  [FindingSeverity.MEDIUM]: '보통',
  [FindingSeverity.HIGH]: '높음',
  [FindingSeverity.CRITICAL]: '심각',
  [FindingSeverity.GOOD]: '우수',
};

/**
 * 위험요인 심각도 → 공용 Badge tone.
 *
 * 4단계가 **서로 다른 색을 갖는 것**이 이 맵이 존재하는 이유다. HIGH를 amber로 접으면
 * MEDIUM과 같은 색이 되어 등급 구분이 사라지므로, tone에 orange 층을 두고 여기서 쓴다
 * (2026-07-28 사용자 확정). 서열: CRITICAL rose > HIGH orange > MEDIUM amber > LOW emerald.
 *
 * GOOD은 위험도가 아니라 "우수 사례"라 LOW와 같은 녹색 끝에 둔다 — 둘이 한 화면에
 * 같이 뜨는 자리는 없다(발견사항은 HAZARD / BEST_PRACTICE로 목록이 갈린다).
 *
 * ⚠️ 운영자가 등급 색을 지정하는 **위험도 배지**(`SeverityBadge`·`EditableRiskBadge`)와는
 * 다른 축이다. 그쪽 색은 DB `RiskAssessmentConfig`에서 오는 런타임 값이라 리터럴 tone으로
 * 표현할 수 없고, 전용 컴포넌트로 유지한다.
 */
const findingSeverityTones: Record<FindingSeverity, BadgeTone> = {
  [FindingSeverity.LOW]: 'emerald',
  [FindingSeverity.MEDIUM]: 'amber',
  [FindingSeverity.HIGH]: 'orange',
  [FindingSeverity.CRITICAL]: 'rose',
  [FindingSeverity.GOOD]: 'emerald',
};

/**
 * 문자열 severity → tone. 서버 원문·스냅샷 필드는 enum이 아니라 `string`으로 흐르는 자리가
 * 많아(보고서 content JSON 등) 좁히는 대신 여기서 받아 넘긴다. 미지의 값은 중립.
 */
export function getFindingSeverityTone(severity: string | null | undefined): BadgeTone {
  return findingSeverityTones[severity as FindingSeverity] ?? 'slate';
}

/**
 * 배지가 아니라 **인라인 글자**로 심각도를 보일 때의 색. `Badge`의 tone 글자색과 같은 층
 * (`text-{tone}-600`)이라 배지 옆에 놓여도 같은 등급이 같은 색으로 보인다.
 *
 * ⚠️ **`findingSeverityTones` 바로 아래 둔 이유**: 사다리를 두 곳에 적으면 갈린다.
 * 실제로 `pages/tbm/new.tsx`가 자체 `SEVERITY_COLORS`를 갖고 있었는데
 * HIGH·CRITICAL이 둘 다 red / MEDIUM이 orange / LOW가 yellow로 **한 칸씩 밀려**,
 * 같은 화면에서 배지와 글자색이 서로 다른 등급을 말하고 있었다(2026-07-28 발견).
 * 등급을 추가·변경하면 **두 맵을 함께** 고칠 것.
 *
 * 리터럴 맵인 이유는 Tailwind 정적 스캔이다(`text-${tone}-600` 같은 조합 금지).
 */
const findingSeverityTextClasses: Record<FindingSeverity, string> = {
  [FindingSeverity.LOW]: 'text-emerald-600',
  [FindingSeverity.MEDIUM]: 'text-amber-600',
  [FindingSeverity.HIGH]: 'text-orange-600',
  [FindingSeverity.CRITICAL]: 'text-rose-600',
  [FindingSeverity.GOOD]: 'text-emerald-600',
};

/** 문자열 severity → 인라인 글자색. 미지의 값은 중립(`getFindingSeverityTone`과 같은 폴백 축). */
export function getFindingSeverityTextClass(severity: string | null | undefined): string {
  return findingSeverityTextClasses[severity as FindingSeverity] ?? 'text-ink-600';
}

/**
 * 점검 상태 → 공용 Badge tone
 */
export const inspectionStatusTones: Record<InspectionStatus, BadgeTone> = {
  [InspectionStatus.SCHEDULED]: 'violet',
  [InspectionStatus.IN_PROGRESS]: 'blue',
  [InspectionStatus.PENDING_REVIEW]: 'amber',
  [InspectionStatus.COMPLETED]: 'emerald',
};

/**
 * 점검 상태 설정 (label + color)
 * 3곳 로컬 statusConfig 대신 이 공통 정의를 사용
 *
 * 아이콘은 여기 없다 — 컴포넌트라 `.ts` 데이터 계층이 들고 있으면 lib → components
 * 역방향 의존이 된다. `components/inspection/inspectionStatusDisplay.ts`의
 * `getInspectionStatusIcon(status)`를 쓸 것.
 */
export const inspectionStatusConfig: Record<
  string,
  { label: string; color: string; tone: BadgeTone }
> = {
  SCHEDULED: { label: '예약됨', color: 'text-violet-600 bg-violet-50', tone: 'violet' },
  DRAFT: { label: '진행중', color: 'text-blue-600 bg-blue-50', tone: 'blue' },
  IN_PROGRESS: { label: '진행중', color: 'text-blue-600 bg-blue-50', tone: 'blue' },
  PENDING_REVIEW: { label: '검토 대기', color: 'text-amber-600 bg-amber-50', tone: 'amber' },
  COMPLETED: { label: '완료', color: 'text-green-600 bg-green-50', tone: 'emerald' },
};

/**
 * 로컬 시간대 기준 YYYY-MM-DD 문자열 반환
 */
export function getLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * 예약 점검의 시작 가능 여부 판별
 * scheduledAt의 날짜(로컬 시간대) ≤ 오늘이면 시작 가능
 */
export function canStartScheduledInspection(scheduledAt?: string | null): boolean {
  if (!scheduledAt) return true;
  const scheduledDate = getLocalDateString(new Date(scheduledAt));
  const today = getLocalDateString(new Date());
  return scheduledDate <= today;
}

/**
 * 이전 점검의 미해결 발견사항 조회
 */
export async function getPreviousUnresolvedFindings(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<PreviousUnresolvedFinding[]>> {
  const response = await api.get<ApiResponse<PreviousUnresolvedFinding[]>>(
    `/inspections/${inspectionId}/previous-unresolved?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 이전 발견사항을 현재 점검에 재발견으로 추가
 */
export async function rediscoverFinding(
  inspectionId: string,
  originalFindingId: string,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.post<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${originalFindingId}/rediscover?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 검수 완료
 */
export async function completeReview(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  const response = await api.post<ApiResponse<Inspection>>(
    `/inspections/${inspectionId}/complete-review?companyId=${companyId}`
  );
  return response.data;
}

/**
 * AI 분석 완료 여부 확인
 */
export async function isAnalysisComplete(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ isComplete: boolean }>> {
  const response = await api.get<ApiResponse<{ isComplete: boolean }>>(
    `/inspections/${inspectionId}/is-analysis-complete?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 검수 상태 한글 라벨
 */
export const reviewStatusLabels: Record<ReviewStatus, string> = {
  [ReviewStatus.PENDING]: '검수 대기',
  [ReviewStatus.COMPLETED]: '검수 완료',
};

/**
 * 검수 상태 색상
 */
export const reviewStatusColors: Record<ReviewStatus, string> = {
  [ReviewStatus.PENDING]: 'bg-orange-100 text-orange-800',
  [ReviewStatus.COMPLETED]: 'bg-green-100 text-green-800',
};

// ============================================
// 공동 점검 관련 API (Phase 2 추가)
// ============================================

// collaborator 역할
export type CollaboratorRole = 'PRIMARY' | 'SECONDARY';

/**
 * 점검 참여자 정보 (기존 InspectionContributor + InspectionParticipant 통합)
 *
 * 서버 스펙:
 * - role: PRIMARY(정 담당자) / SECONDARY(부) — NOT NULL
 * - leftAt / excludedAt 둘 다 null이어야 활성 참여자
 */
export interface InspectionCollaborator {
  id: string;
  inspectionId: string;
  userId: string;
  /** 서버에서 계산되는 표시용 이름 (user.name 기반). 누락 시 user.name으로 fallback. */
  userName?: string;
  role: CollaboratorRole;
  profileImageUrl?: string;
  joinedAt: string;
  leftAt?: string | null;
  reviewStatus: ReviewStatus;
  reviewedAt?: string | null;
  // 점검완료 서명 필드
  completionStatus?: 'IN_PROGRESS' | 'COMPLETED';
  completedAt?: string | null;
  signerName?: string | null;
  signerTitle?: string | null;
  signatureData?: string | null;
  signatureMethod?: string | null;
  excludedAt?: string | null;
  excludedBy?: string | null;
  excludeReason?: string | null;
  /** 참여 시점 스냅샷 이름 (서버 신설 필드, 탈퇴/purge 후에도 원본 유지) */
  joinSnapshotName?: string | null;
  /** 사진 업로드 기여 (실시간 계산) */
  hasPhotoContribution?: boolean;
  /** 개선대책 선택 기여 (실시간 계산) */
  hasMeasureContribution?: boolean;
  /** 서버 include 시에만 포함 (Inspection 상세 응답) */
  user?: {
    id: string;
    deletedAt?: string | null;
    member?: {
      name: string;
    };
    auths?: Array<{ provider?: string; providerId: string }>;
  };
}

// 진행 중 점검 정보 (현장별)
export interface InProgressInspection {
  id: string;
  siteId: string;
  siteName: string;
  inspectorId: string;
  inspectorName: string;
  status: InspectionStatus;
  photoCount: number;
  findingCount: number;
  collaboratorCount: number;
  startedAt: string;
}

// 해결 증빙 입력
export interface ResolveWithEvidenceInput {
  resolutionPhotoUrl?: string;
  resolutionPhotoId?: string;
  resolutionNotes?: string;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
}

// 업로드된 파일 응답
export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
  purpose: string;
  createdAt: string;
}

/**
 * 해결 증빙 사진 업로드
 */
export async function uploadResolutionPhoto(
  file: File,
  companyId: string
): Promise<ApiResponse<UploadedFile>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('purpose', 'INSPECTION_PHOTO');
  formData.append('companyId', companyId);

  const response = await api.post<ApiResponse<UploadedFile>>(
    '/files/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * 현장별 진행 중 점검 조회
 * 서버는 Prisma raw 객체(inspector, _count, site 포함)를 반환하므로 프론트 InProgressInspection으로 매핑.
 */
interface InProgressInspectionServerRaw {
  id: string;
  siteId: string;
  site: { id: string; name: string };
  inspectorId: string;
  inspector: { id: string; member: { name: string } };
  status: string;
  startedAt?: string;
  createdAt: string;
  _count: { photos: number; findings: number };
  collaborators: Array<{ id: string }>;
}

export async function getInProgressForSite(
  siteId: string,
  companyId: string
): Promise<ApiResponse<InProgressInspection[]>> {
  const response = await api.get<ApiResponse<InProgressInspectionServerRaw[]>>(
    `/inspections/in-progress?siteId=${siteId}&companyId=${companyId}`
  );
  const raw = response.data;
  if (!raw.success || !raw.data) {
    return { success: raw.success, data: null, error: raw.error };
  }
  const mapped: InProgressInspection[] = raw.data.map((i) => ({
    id: i.id,
    siteId: i.siteId,
    siteName: i.site?.name ?? '',
    inspectorId: i.inspectorId,
    // 담당자 = PRIMARY collaborator의 signerName ?? joinSnapshotName 우선, 없으면 라이브 라벨
    inspectorName: getEvidenceDisplayName(
      (() => {
        const primary = ((i as any).collaborators as Array<{ role?: string; signerName?: string | null; joinSnapshotName?: string | null }> | undefined)
          ?.find((c) => c.role === 'PRIMARY');
        return primary?.signerName ?? primary?.joinSnapshotName ?? null;
      })(),
      { name: i.inspector?.member?.name },
    ),
    status: i.status as InspectionStatus,
    photoCount: i._count?.photos ?? 0,
    findingCount: i._count?.findings ?? 0,
    collaboratorCount: i.collaborators?.length ?? 1,
    startedAt: i.startedAt ?? i.createdAt,
  }));
  return { success: true, data: mapped, error: null };
}

/**
 * 점검 참여 (함께 점검)
 */
export async function joinInspection(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<InspectionCollaborator>> {
  const response = await api.post<ApiResponse<InspectionCollaborator>>(
    `/inspections/${inspectionId}/join?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 점검 나가기
 */
export async function leaveInspection(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ success: boolean }>> {
  const response = await api.post<ApiResponse<{ success: boolean }>>(
    `/inspections/${inspectionId}/leave?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 내 검수 완료
 */
export async function completeMyReview(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<InspectionCollaborator>> {
  const response = await api.post<ApiResponse<InspectionCollaborator>>(
    `/inspections/${inspectionId}/complete-my-review?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 개별 사진 검토 상태 업데이트
 */
export async function updatePhotoReview(
  inspectionId: string,
  photoId: string,
  isReviewed: boolean,
  companyId: string
): Promise<ApiResponse<InspectionPhoto>> {
  const response = await api.patch<ApiResponse<InspectionPhoto>>(
    `/inspections/${inspectionId}/photos/${photoId}/review?companyId=${companyId}`,
    { isReviewed }
  );
  return response.data;
}

/**
 * 일괄 검토 완료 - 분석 완료된 모든 사진을 검토 완료 처리
 */
export async function bulkReviewPhotos(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ updatedCount: number; photos: InspectionPhoto[] }>> {
  const response = await api.post<ApiResponse<{ updatedCount: number; photos: InspectionPhoto[] }>>(
    `/inspections/${inspectionId}/photos/bulk-review?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 발견사항 해결 (증빙 포함)
 */
export async function resolveWithEvidence(
  inspectionId: string,
  findingId: string,
  data: ResolveWithEvidenceInput,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/resolve-with-evidence?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 참여자 목록 조회
 */
export async function getContributors(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<InspectionCollaborator[]>> {
  const response = await api.get<ApiResponse<InspectionCollaborator[]>>(
    `/inspections/${inspectionId}/contributors?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 사진 재분석 요청 (단일)
 *
 * ⚠️ **재분석은 그 사진의 발견사항을 전량 삭제하고 새로 만든다**(서버
 * `photo-analysis.processor.ts` 「재분석: 기존 이력 삭제」). 사용자가 손댄 것(편집·개선대책 선택)도
 * 사라지므로, 부르는 쪽이 그 사실을 알린 뒤에 불러야 한다.
 *
 * `options.analysisRegion`은 **세 상태가 서로 다르다**(서버 `ReanalyzePhotoDto` 계약):
 *
 * | 넘긴 값 | 뜻 |
 * |---|---|
 * | `options` 생략 또는 `analysisRegion` 미전달 | 저장된 영역을 **그대로 유지** |
 * | `null` | 영역 **해제** → 다음 분석은 전체 분석 |
 * | `{x,y,w,h}` | 그 영역으로 **교체** |
 *
 * ⚠️ **`?? null`로 「단순화」하지 말 것** — 그러면 영역을 안 넘긴 기존 호출부(분석 실패 재시도 등)가
 * 사용자가 지정한 영역을 **조용히 지운다**. 미전달이 유지로 남는 것은 `JSON.stringify`가 `undefined`
 * 값을 빼기 때문이고, 그래서 아래처럼 값을 그대로 담아도 서버는 「키 없음」으로 받는다.
 */
export async function reanalyzePhoto(
  inspectionId: string,
  photoId: string,
  companyId: string,
  options?: { analysisRegion?: AnalysisRegion | null }
): Promise<ApiResponse<InspectionPhoto>> {
  const response = await api.post<ApiResponse<InspectionPhoto>>(
    `/inspections/${inspectionId}/photos/${photoId}/reanalyze?companyId=${companyId}`,
    { analysisRegion: options?.analysisRegion }
  );
  return response.data;
}

/**
 * 사진 일괄 재분석 요청 (복수)
 */
export async function bulkReanalyzePhotos(
  inspectionId: string,
  photoIds: string[],
  companyId: string
): Promise<ApiResponse<{ queuedCount: number; analysisMode: string; jobCount: number }>> {
  const response = await api.post<ApiResponse<{ queuedCount: number; analysisMode: string; jobCount: number }>>(
    `/inspections/${inspectionId}/photos/reanalyze?companyId=${companyId}`,
    { photoIds }
  );
  return response.data;
}

/**
 * 분석 실패한 모든 사진 일괄 재분석 요청
 */
export async function bulkReanalyzeFailedPhotos(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ queuedCount: number; message: string; jobIds: string[] }>> {
  const response = await api.post<ApiResponse<{ queuedCount: number; message: string; jobIds: string[] }>>(
    `/inspections/${inspectionId}/photos/bulk-reanalyze?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 사진 삭제
 */
export async function deletePhoto(
  inspectionId: string,
  photoId: string,
  companyId: string
): Promise<ApiResponse<null>> {
  const response = await api.delete<ApiResponse<null>>(
    `/inspections/${inspectionId}/photos/${photoId}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 사진 일괄 삭제
 */
export async function deletePhotos(
  inspectionId: string,
  photoIds: string[],
  companyId: string
): Promise<ApiResponse<{ deletedCount: number }>> {
  const response = await api.delete<ApiResponse<{ deletedCount: number }>>(
    `/inspections/${inspectionId}/photos?companyId=${companyId}`,
    { data: { photoIds } }
  );
  return response.data;
}

/**
 * 개선대책 보고서 포함 여부 토글
 */
export async function toggleMeasureSelection(
  inspectionId: string,
  findingId: string,
  measure: string,
  selected: boolean,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/measure-selection?companyId=${companyId}`,
    { measure, selected }
  );
  return response.data;
}

/**
 * 발견사항 미해결로 되돌리기
 */
export async function unresolveFinding(
  inspectionId: string,
  findingId: string,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/unresolve?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 이전 발견사항 조치결과 제출
 * - 미해결: notes만 저장
 * - 해결됨: notes + 해결 사진 저장 (HIGH/CRITICAL은 사진 필수)
 */
export interface SubmitActionResultInput {
  isResolved: boolean;
  notes?: string | null;
  resolutionPhotoUrl?: string | null;
  resolutionPhotoId?: string | null;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }> | null;
  resolvedAt?: string | null;
}

export async function submitActionResult(
  inspectionId: string,
  findingId: string,
  data: SubmitActionResultInput,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/action-result?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 보고서 편집 시 이전 발견사항 조치결과 업데이트
 * 잠금 체크 없음 (보고서 편집 컨텍스트)
 */
export async function updateActionResultForReportEdit(
  inspectionId: string,
  findingId: string,
  data: SubmitActionResultInput,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.patch<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/report-action-result?companyId=${companyId}`,
    data
  );
  return response.data;
}

// ============================================
// 개선대책/관련법규 관리 API
// ============================================

/**
 * 개선대책 추가
 * @param measureId HazardKnowledge ID (선택 시 법적 근거 연동)
 */
export async function addMeasure(
  inspectionId: string,
  findingId: string,
  measure: string,
  companyId: string,
  measureId?: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.post<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/measures?companyId=${companyId}`,
    { measure, measureId }
  );
  return response.data;
}

/**
 * 개선대책 삭제
 */
export async function removeMeasure(
  inspectionId: string,
  findingId: string,
  index: number,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.delete<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/measures/${index}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 관련법규 추가
 * @param measures 연결할 개선대책 배열 (피드백 생성용)
 */
export async function addLegalBasis(
  inspectionId: string,
  findingId: string,
  legalBasis: { type: string; article?: string; summary: string; url?: string; regulationId?: string },
  companyId: string,
  measures?: string[]
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.post<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/legal-basis?companyId=${companyId}`,
    { ...legalBasis, measures }
  );
  return response.data;
}

/**
 * 관련법규 삭제
 */
export async function removeLegalBasis(
  inspectionId: string,
  findingId: string,
  index: number,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.delete<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/legal-basis/${index}?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 발견사항(위험요인) 복원
 */
export async function restoreFinding(
  inspectionId: string,
  findingId: string,
  companyId: string
): Promise<ApiResponse<InspectionFinding>> {
  const response = await api.post<ApiResponse<InspectionFinding>>(
    `/inspections/${inspectionId}/findings/${findingId}/restore?companyId=${companyId}`
  );
  return response.data;
}

// ============================================
// 주 담당자 변경 및 검토 요청 API
// ============================================

// 현장 담당자 정보
export interface SiteAssignee {
  id: string;
  name: string;
  phone: string | null;
}

/**
 * 현장 담당자 목록 조회 (주 담당자 변경용)
 */
export async function getSiteAssignees(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<SiteAssignee[]>> {
  const response = await api.get<ApiResponse<SiteAssignee[]>>(
    `/inspections/${inspectionId}/assignees?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 주 담당자 변경
 */
export async function changeInspector(
  inspectionId: string,
  newInspectorId: string,
  companyId: string
): Promise<ApiResponse<Inspection>> {
  const response = await api.patch<ApiResponse<Inspection>>(
    `/inspections/${inspectionId}/inspector?companyId=${companyId}`,
    { newInspectorId }
  );
  return response.data;
}

// 검토 요청 응답
export interface ReviewRequestResult {
  notifiedUsers: Array<{
    id: string;
    name: string;
    pendingCount: number;
  }>;
  totalPending: number;
}

/**
 * 검토 요청 (미검토 사진이 있는 참여자에게 알림)
 */
export async function requestReview(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<ReviewRequestResult>> {
  const response = await api.post<ApiResponse<ReviewRequestResult>>(
    `/inspections/${inspectionId}/request-review?companyId=${companyId}`
  );
  return response.data;
}

// ============================================
// 병렬 업로드 API (R2 직접 업로드)
// ============================================

/** 업로드 준비 요청 */
export interface PrepareUploadRequest {
  files: Array<{
    filename: string;
    mimeType: string;
  }>;
}

/** 업로드 준비 응답 */
export interface PrepareUploadResponse {
  uploadType: 'direct' | 'server';
  uploads: Array<{
    photoId: string;
    uploadUrl?: string;
    storagePath?: string;
    publicUrl?: string;
  }>;
}

/** 업로드 완료 응답 */
export interface UploadCompleteResponse {
  id: string;
  url: string;
  status: string;
  analysisStatus: string;
}

/** 개별 업로드 진행 상태 */
export interface UploadProgress {
  photoId: string;
  filename: string;
  status: 'pending' | 'uploading' | 'completing' | 'completed' | 'failed';
  error?: string;
}

/**
 * 사진 업로드 준비 (Presigned URL 획득)
 * R2 환경: uploadType='direct' + presigned URL 반환
 * 로컬 환경: uploadType='server' 반환
 */
export async function preparePhotoUpload(
  inspectionId: string,
  // ⚠️ `analysisRegion`은 **이 자리**에 실려야 한다 — 직접 업로드(R2) 경로는 사진 행을 prepare
  // 단계에서 만들므로(`preparePhotoUpload`) 업로드 완료 알림(`upload-complete`)에 실으면 늦다.
  // prod는 항상 이 경로다.
  // ⚠️ `detailLocationId`는 2026-10-05에 **선언만** 더했다 — 호출부(`uploadPhotosWithProgress`)가
  // 스프레드로 싣고 서버 DTO가 받아 동작은 했지만, 스프레드는 excess property 검사를 타지 않아
  // tsc가 그 누락을 못 봤다(이 타입에 없는 키를 조용히 보내고 있었다).
  files: Array<{ filename: string; mimeType: string; focusCategories?: string[]; analysisRegion?: AnalysisRegion | null; locationId?: string; detailLocationId?: string; equipmentId?: string; processType?: string }>,
  companyId: string
): Promise<ApiResponse<PrepareUploadResponse>> {
  const response = await api.post<ApiResponse<PrepareUploadResponse>>(
    `/inspections/${inspectionId}/photos/prepare-upload?companyId=${companyId}`,
    { files }
  );
  return response.data;
}

/**
 * 사진 업로드 완료 알림 (R2 직접 업로드 후 호출)
 * @param options.skipAnalysis true면 AI 분석 없이 사진만 등록
 * @param options.focusCategories AI 분석 중점 카테고리 코드 배열
 *
 * ⚠️ **`purpose`가 여기만 1멤버(`BEST_PRACTICE`)인 것은 누락이 아니라 서버와의 일치**다 —
 * `InspectionsController`의 `completePhotoUpload`가 `BEST_PRACTICE`만 매핑하고 나머지는
 * `undefined`로 떨어뜨리므로, 다른 값을 보내면 **400이 아니라 조용히 DB default**가 된다.
 * 그래서 shared의 `PhotoPurposeSettable`(비기본 전체)로 **넓히지 말 것** — 넓히면 보낼 수 없는
 * 값을 보낼 수 있는 것처럼 보인다. 입구 비대칭(`addPhoto`는 `OVERVIEW_PHOTO`도 매핑한다)이
 * 해소되면 그때 이 자리도 함께 본다.
 */
export async function completePhotoUpload(
  inspectionId: string,
  photoId: string,
  companyId: string,
  options?: { skipAnalysis?: boolean; purpose?: 'BEST_PRACTICE'; focusCategories?: string[] }
): Promise<ApiResponse<UploadCompleteResponse>> {
  const params = new URLSearchParams({ companyId });
  if (options?.skipAnalysis) {
    params.append('skipAnalysis', 'true');
  }
  if (options?.purpose) {
    params.append('purpose', options.purpose);
  }
  if (options?.focusCategories && options.focusCategories.length > 0) {
    params.append('focusCategories', options.focusCategories.join(','));
  }
  const response = await api.post<ApiResponse<UploadCompleteResponse>>(
    `/inspections/${inspectionId}/photos/${photoId}/upload-complete?${params.toString()}`
  );
  return response.data;
}

/**
 * R2에 직접 파일 업로드 (Presigned URL 사용)
 */
async function uploadFileToR2(
  file: File,
  uploadUrl: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`R2 업로드 실패: ${response.status} ${response.statusText}`);
  }
}

/**
 * 병렬 사진 업로드 (R2 직접 업로드 지원)
 *
 * @param inspectionId 점검 ID
 * @param files 업로드할 파일 배열
 * @param companyId 조직 ID
 * @param onProgress 개별 파일 진행 상태 콜백
 * @param options.skipAnalysis true면 AI 분석 없이 사진만 등록
 * @param options.focusCategories AI 분석 중점 카테고리 코드 배열 (미지정 시 전체 분석)
 * @returns 업로드 결과 배열
 */
export async function uploadPhotosParallel(
  inspectionId: string,
  files: File[],
  companyId: string,
  onProgress?: (progress: UploadProgress[]) => void,
  options?: {
    skipAnalysis?: boolean;
    /**
     * 1멤버인 이유는 `completePhotoUpload` JSDoc이 소유한다(이 경로가 그것을 호출한다 —
     * 넓히면 보낼 수 없는 값을 보낼 수 있는 것처럼 보인다).
     */
    purpose?: 'BEST_PRACTICE';
    focusCategories?: string[];              // inspection-level (legacy fallback)
    perPhotoFocusCategories?: Map<number, string[]>; // per-photo categories
    /**
     * 사진별 중점 분석 영역 — 지정한 사진만 그 사각형을 표시해 그 안을 분석한다.
     *
     * ⚠️ **점검 단위 폴백을 두지 않는다**(카테고리와 다른 점). 영역은 사진마다 피사체 위치가 다른
     * 고유 속성이라, 한 사진의 좌표를 다른 사진에 적용하면 **엉뚱한 자리를 강조한다**
     * (결정 2026-10-05: 「영역은 사진 고유 속성이라 점검 단위 폴백은 두지 않는다」).
     */
    perPhotoAnalysisRegions?: Map<number, AnalysisRegion | null>;
    locationId?: string | null;
    equipmentId?: string | null;
    perPhotoLocationIds?: Map<number, string | null>; // per-photo location
    perPhotoEquipmentIds?: Map<number, string | null>; // per-photo equipment
    perPhotoDetailLocationIds?: Map<number, string | null>; // per-photo detail location
    perPhotoSkipAnalysis?: Map<number, boolean>;        // per-photo skip AI analysis
    perPhotoProcessTypes?: Map<number, string | null>;  // per-photo process type
  }
): Promise<{
  success: InspectionPhoto[];
  failed: Array<{ filename: string; error: string }>;
}> {
  const results: InspectionPhoto[] = [];
  const failed: Array<{ filename: string; error: string }> = [];

  // 진행 상태 초기화
  const progressState: UploadProgress[] = files.map((file) => ({
    photoId: '',
    filename: file.name,
    status: 'pending' as const,
  }));

  const updateProgress = (index: number, update: Partial<UploadProgress>) => {
    progressState[index] = { ...progressState[index], ...update };
    onProgress?.([...progressState]);
  };

  // 1. 업로드 준비 (Presigned URL 획득)
  // per-photo focusCategories를 prepare 단계에서 전달하여 DB에 저장
  const prepareResponse = await preparePhotoUpload(
    inspectionId,
    files.map((f, index) => ({
      filename: f.name,
      mimeType: f.type,
      focusCategories: options?.perPhotoFocusCategories?.get(index) ?? options?.focusCategories,
      ...(() => {
        const loc = options?.perPhotoLocationIds?.has(index)
          ? options.perPhotoLocationIds.get(index)
          : options?.locationId;
        const eq = options?.perPhotoEquipmentIds?.has(index)
          ? options.perPhotoEquipmentIds.get(index)
          : options?.equipmentId;
        const detailLoc = options?.perPhotoDetailLocationIds?.has(index)
          ? options.perPhotoDetailLocationIds.get(index)
          : undefined;
        const pt = options?.perPhotoProcessTypes?.has(index)
          ? options.perPhotoProcessTypes.get(index)
          : undefined;
        // 새 사진이라 「해제」할 영역이 없다 — 지정된 것만 싣는다(점검 단위 폴백 없음).
        const region = options?.perPhotoAnalysisRegions?.get(index);
        return {
          ...(loc ? { locationId: loc } : {}),
          ...(eq ? { equipmentId: eq } : {}),
          ...(detailLoc ? { detailLocationId: detailLoc } : {}),
          ...(pt ? { processType: pt } : {}),
          ...(region ? { analysisRegion: region } : {}),
        };
      })(),
    })),
    companyId
  );

  if (!prepareResponse.success || !prepareResponse.data) {
    // 준비 실패 시 모든 파일 실패 처리
    return {
      success: [],
      failed: files.map((f) => ({
        filename: f.name,
        error: prepareResponse.error?.message || '업로드 준비 실패',
      })),
    };
  }

  const { uploadType, uploads } = prepareResponse.data;

  // photoId 업데이트
  uploads.forEach((upload, index) => {
    updateProgress(index, { photoId: upload.photoId });
  });

  if (uploadType === 'direct') {
    // R2 직접 업로드 (병렬)
    const uploadPromises = files.map(async (file, index) => {
      const upload = uploads[index];
      if (!upload.uploadUrl) {
        updateProgress(index, { status: 'failed', error: 'Presigned URL 없음' });
        failed.push({ filename: file.name, error: 'Presigned URL 없음' });
        return;
      }

      try {
        // R2에 업로드
        updateProgress(index, { status: 'uploading' });
        await uploadFileToR2(file, upload.uploadUrl);

        // 서버에 완료 알림
        // focusCategories는 prepare 단계에서 이미 DB에 저장되므로 여기서는 전달 불필요
        updateProgress(index, { status: 'completing' });
        const photoSkipAnalysis = options?.perPhotoSkipAnalysis?.has(index)
          ? options.perPhotoSkipAnalysis.get(index)
          : options?.skipAnalysis;
        const completeResponse = await completePhotoUpload(
          inspectionId,
          upload.photoId,
          companyId,
          {
            skipAnalysis: photoSkipAnalysis,
            purpose: options?.purpose,
          }
        );

        if (completeResponse.success && completeResponse.data) {
          updateProgress(index, { status: 'completed' });
          results.push({
            id: completeResponse.data.id,
            inspectionId,
            url: completeResponse.data.url,
            status: completeResponse.data.status,
            analysisStatus: completeResponse.data.analysisStatus as AnalysisStatus,
            isReviewed: false,
            createdAt: new Date().toISOString(),
          });
        } else {
          const errorMsg = completeResponse.error?.message || '완료 처리 실패';
          updateProgress(index, { status: 'failed', error: errorMsg });
          failed.push({ filename: file.name, error: errorMsg });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '업로드 실패';
        updateProgress(index, { status: 'failed', error: errorMsg });
        failed.push({ filename: file.name, error: errorMsg });
      }
    });

    await Promise.all(uploadPromises);
  } else {
    // 서버 업로드 (로컬 환경 - 기존 방식이지만 병렬로)
    const uploadPromises = files.map(async (file, index) => {
      try {
        updateProgress(index, { status: 'uploading' });
        const resolvedCategories =
          options?.perPhotoFocusCategories?.get(index) ??
          options?.focusCategories;
        const resolvedSkipAnalysis = options?.perPhotoSkipAnalysis?.has(index)
          ? options.perPhotoSkipAnalysis.get(index)
          : options?.skipAnalysis;
        const resolvedLocationId = options?.perPhotoLocationIds?.has(index)
          ? options.perPhotoLocationIds.get(index)
          : options?.locationId;
        const resolvedEquipmentId = options?.perPhotoEquipmentIds?.has(index)
          ? options.perPhotoEquipmentIds.get(index)
          : options?.equipmentId;
        const resolvedDetailLocationId = options?.perPhotoDetailLocationIds?.has(index)
          ? options.perPhotoDetailLocationIds.get(index)
          : undefined;
        // 이 분기는 로컬 storage(dev)에서만 돈다 — 빼먹으면 prod는 정상인 채 dev에서만 영역이
        // 조용히 빠져, 「영역이 안 먹는다」를 서버에서 찾게 된다.
        const resolvedRegion = options?.perPhotoAnalysisRegions?.get(index);
        const response = await uploadPhoto(inspectionId, file, companyId, {
          skipAnalysis: resolvedSkipAnalysis,
          purpose: options?.purpose,
          focusCategories: resolvedCategories,
          analysisRegion: resolvedRegion ?? undefined,
          locationId: resolvedLocationId ?? undefined,
          equipmentId: resolvedEquipmentId ?? undefined,
          detailLocationId: resolvedDetailLocationId ?? undefined,
        });

        if (response.success && response.data) {
          updateProgress(index, { status: 'completed' });
          results.push(response.data);
        } else {
          const errorMsg = response.error?.message || '업로드 실패';
          updateProgress(index, { status: 'failed', error: errorMsg });
          failed.push({ filename: file.name, error: errorMsg });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '업로드 실패';
        updateProgress(index, { status: 'failed', error: errorMsg });
        failed.push({ filename: file.name, error: errorMsg });
      }
    });

    await Promise.all(uploadPromises);
  }

  return { success: results, failed };
}

// ============================================
// 점검 이름 생성 유틸리티
// ============================================

/**
 * 점검 이름 생성
 * 형식: "{현장명} {N}차 점검"
 */
export function getInspectionName(
  siteName: string,
  sequenceNumber?: number
): string {
  const seq = sequenceNumber || 1;
  return `${siteName} ${seq}차 점검`;
}

/**
 * 점검 객체에서 이름 생성
 */
export function formatInspectionName(
  inspection: { site?: { name: string } | null; sequenceNumber?: number }
): string {
  const siteName = inspection.site?.name || '현장';
  return getInspectionName(siteName, inspection.sequenceNumber);
}

// ============================================
// 사진 어노테이션 (그리기)
// ============================================

// 그리기 스트로크 타입
export interface AnnotationStroke {
  id: string;
  points: Array<{ x: number; y: number; pressure?: number }>;
  color: string;
  width: number;
  timestamp: number;
}

// 어노테이션 데이터 타입
export interface AnnotationData {
  version: 1;
  strokes: AnnotationStroke[];
}

// 어노테이션 응답 타입
export interface PhotoAnnotation {
  id: string;
  photoId: string;
  data: AnnotationData;
  createdById: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 사진 어노테이션 조회
 */
export async function getPhotoAnnotation(
  companyId: string,
  inspectionId: string,
  photoId: string
): Promise<PhotoAnnotation | null> {
  const response = await api.get(
    `/inspections/${inspectionId}/photos/${photoId}/annotation`,
    { params: { companyId } }
  );
  return response.data.data;
}

/**
 * 사진 어노테이션 저장
 */
export async function savePhotoAnnotation(
  companyId: string,
  inspectionId: string,
  photoId: string,
  data: AnnotationData
): Promise<PhotoAnnotation> {
  const response = await api.patch(
    `/inspections/${inspectionId}/photos/${photoId}/annotation`,
    { data },
    { params: { companyId } }
  );
  return response.data.data;
}

/**
 * 사진 어노테이션 삭제
 */
export async function deletePhotoAnnotation(
  companyId: string,
  inspectionId: string,
  photoId: string
): Promise<void> {
  await api.delete(
    `/inspections/${inspectionId}/photos/${photoId}/annotation`,
    { params: { companyId } }
  );
}

// ============================================
// 지식베이스 매칭 관련
// ============================================

/** 지식베이스 매칭 결과 */
export interface KnowledgeMatch {
  id: string;
  knowledgeId: string;
  matchScore: number;
  matchReason: string | null;
  matchRank: number | null;
  userAction: 'ACCEPT' | 'MODIFY' | 'REJECT' | null;
  knowledge: {
    id: string;
    category: string;
    subCategory: string | null;
    hazardFactor: string | null;
    knowledgeMeasures: Array<{
      id: string;
      content: string;
      sortOrder: number;
    }>;
  };
}

/**
 * Finding에 대한 지식베이스 매칭 결과 조회
 */
export async function getKnowledgeMatchesForFinding(
  findingId: string
): Promise<KnowledgeMatch[]> {
  const response = await api.get<ApiResponse<KnowledgeMatch[]>>(
    `/knowledge/matches/finding/${findingId}`
  );
  return response.data.data || [];
}

/**
 * 지식베이스 매칭에 대한 사용자 피드백 저장
 */
export async function saveKnowledgeMatchFeedback(
  matchId: string,
  action: 'ACCEPT' | 'MODIFY' | 'REJECT',
  feedback?: string
): Promise<void> {
  await api.post(`/knowledge/matches/${matchId}/feedback`, {
    action,
    feedback,
  });
}

// ============================================================
// 점검완료 서명 워크플로우
// ============================================================

export interface CompleteMyInspectionInput {
  signerTitle?: string;
  signatureData: string;
}

export interface CompleteMyInspectionResponse {
  contributor: {
    id: string;
    completionStatus: string;
    completedAt: string;
    user: { id: string; name: string };
  };
  allCompleted: boolean;
  pendingContributors: Array<{ id: string; name: string; role: string }>;
}

/**
 * 점검완료 서명 (본인)
 */
export async function completeMyInspection(
  inspectionId: string,
  companyId: string,
  data: CompleteMyInspectionInput
): Promise<ApiResponse<CompleteMyInspectionResponse>> {
  const response = await api.post<ApiResponse<CompleteMyInspectionResponse>>(
    `/inspections/${inspectionId}/complete-my-inspection?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 점검완료 취소 (본인)
 */
export async function cancelMyCompletion(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<unknown>> {
  const response = await api.post<ApiResponse<unknown>>(
    `/inspections/${inspectionId}/cancel-my-completion?companyId=${companyId}`
  );
  return response.data;
}

/**
 * 점검완료 요청 (미완료 참여자에게 알림)
 */
export async function requestCompletion(
  inspectionId: string,
  companyId: string
): Promise<ApiResponse<{ requestedCount: number; pendingContributors: Array<{ id: string; name: string }> }>> {
  const response = await api.post<ApiResponse<{ requestedCount: number; pendingContributors: Array<{ id: string; name: string }> }>>(
    `/inspections/${inspectionId}/request-completion?companyId=${companyId}`
  );
  return response.data;
}

export interface ExcludeContributorInput {
  contributorId: string;
  reason?: string;
  assigneeUserId: string;
}

/**
 * 참여자 제외 + 항목 인수
 */
export async function excludeContributor(
  inspectionId: string,
  companyId: string,
  data: ExcludeContributorInput
): Promise<ApiResponse<{ success: boolean }>> {
  const response = await api.post<ApiResponse<{ success: boolean }>>(
    `/inspections/${inspectionId}/exclude-contributor?companyId=${companyId}`,
    data
  );
  return response.data;
}

/**
 * 스케줄 변경 요청 생성 (안전관리자용)
 * POST /inspections/:id/schedule-change-request
 */
export async function createScheduleChangeRequest(
  inspectionId: string,
  data: CreateScheduleChangeRequestInput
): Promise<ApiResponse<ScheduleChangeRequest>> {
  const response = await api.post<ApiResponse<ScheduleChangeRequest>>(
    `/inspections/${inspectionId}/schedule-change-request`,
    data
  );
  return response.data;
}

/**
 * 스케줄 변경 요청 응답 (Owner용 - 승인/거부)
 * PATCH /inspections/:id/schedule-change-request/:requestId/respond
 */
export async function respondScheduleChangeRequest(
  inspectionId: string,
  requestId: string,
  data: RespondScheduleChangeRequestInput
): Promise<ApiResponse<{ success: boolean; approved: boolean }>> {
  const response = await api.patch<ApiResponse<{ success: boolean; approved: boolean }>>(
    `/inspections/${inspectionId}/schedule-change-request/${requestId}/respond`,
    data
  );
  return response.data;
}

/**
 * 스케줄 변경 요청 이력 조회
 * GET /inspections/:id/schedule-change-requests
 */
export async function getScheduleChangeRequests(
  inspectionId: string
): Promise<ApiResponse<ScheduleChangeRequest[]>> {
  const response = await api.get<ApiResponse<ScheduleChangeRequest[]>>(
    `/inspections/${inspectionId}/schedule-change-requests`
  );
  return response.data;
}

export interface PhotoMetadataUpdate {
  locationId?: string | null;
  equipmentId?: string | null;
  detailLocationId?: string | null;
  processType?: string | null;
}

export interface PhotoMetadataResult {
  id: string;
  locationId: string | null;
  equipmentId: string | null;
  detailLocationId: string | null;
  processType?: string | null;
  location?: { id: string; name: string } | null;
  detailLocation?: { id: string; name: string } | null;
  equipment?: { id: string; name: string; managementNumber: string | null; processType: string | null } | null;
}

export async function updatePhotoMetadata(
  inspectionId: string,
  photoId: string,
  companyId: string,
  data: PhotoMetadataUpdate,
): Promise<ApiResponse<PhotoMetadataResult>> {
  const response = await api.patch<ApiResponse<PhotoMetadataResult>>(
    `/inspections/${inspectionId}/photos/${photoId}/metadata?companyId=${companyId}`,
    data,
  );
  return response.data;
}

// ============================================
// InspectionSiteContact (점검 회차별 현장 담당자) API
// ============================================

export interface InspectionSiteContact {
  inspectionId: string;
  siteContactId: string;
  signatureData: string | null;
  signatureMethod: string | null;
  signedAt: string | null;
  createdAt: string;
  siteContact: {
    id: string;
    name: string;
    phone: string;
    department?: string | null;
    position?: string | null;
    email?: string | null;
    isActive: boolean;
  };
}

export async function getInspectionSiteContacts(
  inspectionId: string,
  companyId: string,
): Promise<InspectionSiteContact[]> {
  const response = await api.get<ApiResponse<InspectionSiteContact[]>>(
    `/inspections/${inspectionId}/site-contacts?companyId=${companyId}`,
  );
  return response.data.data ?? [];
}

export async function addInspectionSiteContacts(
  inspectionId: string,
  companyId: string,
  siteContactIds: string[],
): Promise<InspectionSiteContact[]> {
  const response = await api.post<ApiResponse<InspectionSiteContact[]>>(
    `/inspections/${inspectionId}/site-contacts?companyId=${companyId}`,
    { siteContactIds },
  );
  return response.data.data ?? [];
}

export async function removeInspectionSiteContact(
  inspectionId: string,
  siteContactId: string,
  companyId: string,
): Promise<void> {
  await api.delete(
    `/inspections/${inspectionId}/site-contacts/${siteContactId}?companyId=${companyId}`,
  );
}

export async function updateInspectionSiteContactSignature(
  inspectionId: string,
  siteContactId: string,
  companyId: string,
  data: { signatureData: string; signatureMethod?: string },
): Promise<InspectionSiteContact> {
  const response = await api.patch<ApiResponse<InspectionSiteContact>>(
    `/inspections/${inspectionId}/site-contacts/${siteContactId}/signature?companyId=${companyId}`,
    data,
  );
  return response.data.data!;
}

export async function clearInspectionSiteContactSignature(
  inspectionId: string,
  siteContactId: string,
  companyId: string,
): Promise<InspectionSiteContact> {
  const response = await api.delete<ApiResponse<InspectionSiteContact>>(
    `/inspections/${inspectionId}/site-contacts/${siteContactId}/signature?companyId=${companyId}`,
  );
  return response.data.data!
}

/** 점검 시작 직후 상세 페이지에서 현장 담당자 모달 자동 오픈 신호용 쿼리 파라미터 */
export const INSPECTION_OPEN_CONTACTS_PARAM = 'openContacts';
export const INSPECTION_OPEN_CONTACTS_VALUE = '1';
export function buildInspectionStartUrl(id: string): string {
  return `/inspections/${id}?${INSPECTION_OPEN_CONTACTS_PARAM}=${INSPECTION_OPEN_CONTACTS_VALUE}`;
}
